import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccessRequest, InviteLink } from '../../../core/auth/auth.models';
import { AuthService } from '../../../core/auth/auth.service';
import { PlatformService } from '../../../core/platform/platform.service';

@Component({
  selector: 'app-access-requests',
  imports: [],
  templateUrl: './access-requests.html',
})
export class AccessRequests implements OnInit {
  private readonly platform = inject(PlatformService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly requests = signal<AccessRequest[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly lastInvite = signal<InviteLink | null>(null);
  protected readonly copied = signal(false);
  protected readonly adminName = this.auth.platformSession()?.name ?? '';

  /** Rótulos em português — a tabela mostrava o enum cru (PENDING/APPROVED/REJECTED). */
  private readonly statusLabels: Record<string, string> = {
    PENDING: 'Pendente',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
  };

  ngOnInit(): void {
    this.load();
  }

  protected statusLabel(status: string): string {
    return this.statusLabels[status] ?? status;
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.platform.listAccessRequests().subscribe({
      next: (list) => {
        this.requests.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Não foi possível carregar as solicitações.');
        this.loading.set(false);
      },
    });
  }

  protected approve(request: AccessRequest): void {
    this.error.set(null);
    this.platform.approve(request.id).subscribe({
      next: (result) => {
        this.showInvite(result);
        this.load();
      },
      error: () => this.error.set('Não foi possível aprovar a solicitação.'),
    });
  }

  protected reject(request: AccessRequest): void {
    this.error.set(null);
    this.platform.reject(request.id).subscribe({
      next: () => this.load(),
      error: () => this.error.set('Não foi possível rejeitar a solicitação.'),
    });
  }

  /** Gera de novo o link de convite: o mostrado na aprovação não sobrevive a um recarregar. */
  protected resendInvite(request: AccessRequest): void {
    this.error.set(null);
    this.platform.resendInvite(request.id).subscribe({
      next: (link) => this.showInvite(link),
      error: (response) =>
        this.error.set(response?.error?.message ?? 'Não foi possível gerar o link de convite.'),
    });
  }

  protected async copyInvite(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2500);
    } catch {
      this.error.set('Não foi possível copiar. Selecione o link e copie manualmente.');
    }
  }

  protected logout(): void {
    this.auth.logoutPlatform();
    this.router.navigate(['/login']);
  }

  private showInvite(link: InviteLink): void {
    this.copied.set(false);
    this.lastInvite.set(link);
  }
}
